<template>
  <div>
    <Title :pages="[{ icon: 'circle', page: 'CONCILIACIÓN BANCARIA' }, { icon: 'circle', page: data ? data.conciliacion.label : '...' }]"></Title>

    <v-container fluid v-if="list_access">
      <ConciliacionSkeleton v-if="loading && !data" />

      <v-card v-if="data">
        <!-- Cabecera -->
        <div class="d-flex align-start px-7 pt-6 pb-4">
          <v-icon color="primary" size="28" class="mr-3 head-icon">account_balance</v-icon>
          <div class="flex-grow-1">
            <div class="title">
              {{ c.money_account.bank ? c.money_account.bank.name + " · " : "" }}{{ c.money_account.name }}
            </div>
            <div class="body-2 grey--text text--darken-1">
              Período {{ formatUtils.formatDateString(c.date_from) }} – {{ formatUtils.formatDateString(c.date_to) }} · {{ c.file_name }}
            </div>
          </div>
          <v-btn icon small @click="dialog_help = true" title="Ayuda" class="mr-1"><v-icon color="grey darken-1">help_outline</v-icon></v-btn>
          <v-btn icon small @click="print" title="Imprimir resumen" class="mr-1" :loading="loading_print"><v-icon color="grey darken-1">print</v-icon></v-btn>
          <v-btn v-if="isClosed && sensible_access" small outlined color="orange darken-2" class="mr-3" @click="dialog_reopen = true">
            <v-icon left small>lock_open</v-icon> Reabrir
          </v-btn>
          <v-chip :color="isDraft ? 'orange lighten-5' : 'green lighten-5'" :text-color="isDraft ? 'orange darken-3' : 'green darken-3'">
            <v-icon small left>{{ isDraft ? "edit_note" : "lock" }}</v-icon>{{ c.state.name }}
          </v-chip>
        </div>

        <KpiBar :summary="data.summary" :dateTo="c.date_to" />

        <!-- Estado de la conciliación -->
        <div v-if="isDraft" class="px-7 pb-3">
          <v-alert v-if="data.close_blockers.length" text dense type="warning" class="mb-0">
            <b>Para guardar la conciliación:</b>
            <ul class="mb-0">
              <li v-for="(b, i) in data.close_blockers" :key="i">{{ b }}</li>
            </ul>
          </v-alert>
          <v-alert v-else text dense type="success" class="mb-0">
            Lista para guardar: todos los movimientos están conciliados o justificados.
          </v-alert>
        </div>

        <v-tabs v-model="tab" class="px-3">
          <v-tab href="#a_conciliar">
            <v-icon left small>rule</v-icon> A conciliar
            <v-chip x-small class="ml-2" :color="data.summary.counts.a_conciliar ? 'primary' : ''" :dark="!!data.summary.counts.a_conciliar">
              {{ data.summary.counts.a_conciliar }}
            </v-chip>
          </v-tab>
          <v-tab href="#conciliados">
            <v-icon left small>link</v-icon> Conciliados
            <v-chip x-small class="ml-2">{{ data.summary.counts.conciliados }}</v-chip>
          </v-tab>
          <v-tab href="#partidas">
            <v-icon left small>pending_actions</v-icon> Resumen y partidas
            <v-chip x-small class="ml-2">{{ data.summary.counts.partidas }}</v-chip>
          </v-tab>
        </v-tabs>
        <v-progress-linear v-if="loading_action" indeterminate height="3" color="primary"></v-progress-linear>
        <v-divider v-else></v-divider>

        <v-tabs-items v-model="tab" class="tabs-visible">
          <v-tab-item value="a_conciliar" class="pa-5">
            <TabAConciliar
              :syscore="data.pending_syscore"
              :lineas="data.pending_lineas"
              :suggestions="data.suggestions"
              :editable="editable"
              :busy="loading_action"
              @match="match"
              @acceptSuggestions="acceptSuggestions"
              @partida="openPartida"
              @createMovement="openCreateMovement"
              @createByRules="createByRules"
              @createRule="createRule"
              @goResumen="tab = 'partidas'"
            />
          </v-tab-item>
          <v-tab-item value="conciliados" class="pa-5">
            <TabConciliados :matches="data.matches" :editable="editable" @unmatch="unmatch" @export="exportTab" />
          </v-tab-item>
          <v-tab-item value="partidas" class="pa-5">
            <TabPartidas
              :partidas="data.partidas"
              :summary="data.summary"
              :dateTo="c.date_to"
              :blockers="data.close_blockers"
              :editable="editable"
              @unpartida="unpartida"
              @export="exportTab"
            />
          </v-tab-item>
        </v-tabs-items>

        <!-- Pie -->
        <v-divider></v-divider>
        <div class="d-flex align-center flex-wrap pa-4">
          <span class="body-2 grey--text text--darken-1">
            <v-icon small>history</v-icon>
            <template v-if="isClosed">Conciliación guardada: {{ formatUtils.formatDateTimeString(c.closed_at) }}</template>
            <template v-else>Guardado automático · {{ formatUtils.formatDateTimeString(c.last_saved_at) }}</template>
          </span>
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="$router.push('/' + section)">Volver</v-btn>
          <v-tooltip v-if="editable" top :disabled="!data.close_blockers.length">
            <template v-slot:activator="{ on }">
              <span v-on="on" class="ml-2">
                <v-btn color="success" :loading="loading_close" :disabled="data.close_blockers.length > 0 || loading_action" @click="dialog_close = true">
                  <v-icon left>task_alt</v-icon> Guardar conciliación
                </v-btn>
              </span>
            </template>
            <div v-for="(b, i) in data.close_blockers" :key="i">{{ b }}</div>
          </v-tooltip>
        </div>
      </v-card>

      <!-- Alta manual de un movimiento desde una línea del banco -->
      <TesoreriaMovementModal
        v-if="tesoreriaCreateAccess && movementItem"
        modal_title="Movimiento de Tesorería"
        formBtn="Crear"
        :dialog="dialog_movement"
        :service="tesoreriaMovementService"
        :item="movementItem"
        :prefill="movementPrefill"
        @updateDialog="(v) => (dialog_movement = v)"
        @updateItem="(v) => (movementItem = v)"
        @created="onMovementCreated"
      />

      <DialogModoMovimiento
        :open="dialog_modo_movimiento"
        :lineas="modo_lineas"
        @accept="onModoMovimiento"
        @cancel="dialog_modo_movimiento = false"
      />

      <DialogMarcarPartida
        :open="dialog_partida"
        :details="partida_details"
        :motivos="data ? data.motivos : []"
        :loading="loading_action"
        @accept="partida"
        @cancel="dialog_partida = false"
      />

      <DialogYesNo
        :open="dialog_close"
        title="Guardar conciliación"
        message="La conciliación queda cerrada y los movimientos conciliados no se podrán revertir. Las partidas conciliatorias pasan a la próxima conciliación de la cuenta."
        nameCancel="Cancelar"
        nameAccept="Guardar"
        @accept="closeConciliacion"
        @cancel="dialog_close = false"
      />

      <DialogYesNo
        :open="dialog_reopen"
        title="Reabrir conciliación"
        message="La conciliación vuelve a borrador para poder modificarla. Solo se puede reabrir la última conciliación de la cuenta."
        nameCancel="Cancelar"
        nameAccept="Reabrir"
        @accept="reopen"
        @cancel="dialog_reopen = false"
      />

      <ModalRegla :dialog="dialog_regla" :item="regla_item" formBtn="Crear" modal_title="Regla de conciliación" :service="conciliacionReglaService" @updateDialog="(v) => (dialog_regla = v)" @loadList="load" />

      <ModalHelp :show="dialog_help" @close="dialog_help = false" />
    </v-container>
  </div>
</template>

<script>
import Constant from "@/constants/sections";
import { conciliacionBancariaService } from "@/libs/ws/conciliacionBancariaService";
import { conciliacionReglaService } from "@/libs/ws/conciliacionReglaService";
import { tesoreriaMovementService } from "@/libs/ws/tesoreriaMovementService";
import { generalService } from "@/libs/ws/generalService";
import { formatUtils } from "@/libs/utils/formatUtils";
import RolMixin from "@/mixins/RolMixin.vue";
import ExportMixin from "@/mixins/ExportMixin.vue";
import Title from "@/components/Title.vue";
import DialogYesNo from "@/components/DialogYesNo.vue";
import TesoreriaMovementModal from "@/views/Treasury/TesoreriaMovements/Modal.vue";
import ModalRegla from "@/views/Treasury/ConciliacionReglas/Modal.vue";
import KpiBar from "./components/KpiBar.vue";
import TabConciliados from "./components/TabConciliados.vue";
import TabAConciliar from "./components/TabAConciliar.vue";
import TabPartidas from "./components/TabPartidas.vue";
import DialogMarcarPartida from "./components/DialogMarcarPartida.vue";
import DialogModoMovimiento from "./components/DialogModoMovimiento.vue";
import ModalHelp from "./components/ModalHelp.vue";
import ConciliacionSkeleton from "./components/ConciliacionSkeleton.vue";

export default {
  components: {
    Title,
    DialogYesNo,
    TesoreriaMovementModal,
    ModalRegla,
    KpiBar,
    TabConciliados,
    TabAConciliar,
    TabPartidas,
    DialogMarcarPartida,
    DialogModoMovimiento,
    ModalHelp,
    ConciliacionSkeleton,
  },
  mixins: [RolMixin, ExportMixin],
  data() {
    return {
      formatUtils,
      section: Constant.SECTION_CONCILIACIONES_BANCARIAS,
      tesoreriaMovementService,
      conciliacionReglaService,
      data: null,
      loading: false,
      loading_action: false,
      loading_close: false,
      loading_print: false,
      tab: "a_conciliar",
      dialog_help: false,
      dialog_close: false,
      dialog_reopen: false,
      dialog_partida: false,
      partida_details: [],
      dialog_movement: false,
      movementItem: null,
      movementPrefill: null,
      movementLineas: [],
      dialog_modo_movimiento: false,
      modo_lineas: [],
      dialog_regla: false,
      regla_item: null,
    };
  },
  computed: {
    id() {
      return parseInt(this.$route.params.id);
    },
    c() {
      return this.data.conciliacion;
    },
    isDraft() {
      return this.data && this.c.state.id === "B";
    },
    isClosed() {
      return this.data && this.c.state.id === "C";
    },
    editable() {
      return this.isDraft && this.update_access;
    },
    tesoreriaCreateAccess() {
      return this.getModuleAccess(Constant.SECTION_TESORERIA_MOVEMENTS, "create");
    },
  },
  mounted() {
    this.load(true);
  },
  methods: {
    load(first = false) {
      this.loading = true;
      return conciliacionBancariaService
        .get(this.id)
        .then((r) => {
          if (!r.status) {
            this.$toast.error(r.msg || r.message, "Error");
            return;
          }
          this.data = r.result;
          // Al abrir: si ya no queda nada por conciliar se muestra el resumen
          if (first && this.data.summary.counts.a_conciliar === 0) this.tab = "partidas";
        })
        .finally(() => { this.loading = false; });
    },
    // Ejecuta una acción del servicio, informa el resultado y recarga la conciliación
    run(promise, onSuccess = null) {
      this.loading_action = true;
      return promise
        .then((r) => {
          if (r.status) {
            if (r.msg) this.$toast.success(r.msg, "OK");
            if (onSuccess) onSuccess(r);
            return this.load();
          }
          this.$toast.error(r.msg || r.message, "Error");
        })
        .catch(() => this.$toast.error("Error al procesar la operación.", "Error"))
        .finally(() => { this.loading_action = false; });
    },
    match(selection) {
      this.run(conciliacionBancariaService.match(this.id, selection));
    },
    unmatch(match) {
      this.run(conciliacionBancariaService.unmatch(this.id, { match_id: match.id }));
    },
    acceptSuggestions(linea_ids = null) {
      this.run(conciliacionBancariaService.acceptSuggestions(this.id, Array.isArray(linea_ids) ? { linea_ids } : {}));
    },
    openPartida(details) {
      this.partida_details = details;
      this.dialog_partida = true;
    },
    partida(motivo) {
      this.run(conciliacionBancariaService.partida(this.id, { detail_ids: this.partida_details.map((d) => d.id), motivo }), () => {
        this.dialog_partida = false;
      });
    },
    unpartida(item) {
      this.run(conciliacionBancariaService.unpartida(this.id, { partida_id: item.partida.id }));
    },
    // Registra todas las líneas que cumplen una regla, un llamado por plantilla
    async createByRules(lineas) {
      const byTemplate = {};
      lineas.forEach((l) => {
        const key = l.regla.tesoreria_movement_template.id + (l.amount > 0 ? "+" : "-");
        (byTemplate[key] = byTemplate[key] || { template_id: l.regla.tesoreria_movement_template.id, ids: [] }).ids.push(l.id);
      });
      this.loading_action = true;
      let ok = 0;
      for (const group of Object.values(byTemplate)) {
        const r = await conciliacionBancariaService.createMovement(this.id, { linea_ids: group.ids, template_id: group.template_id });
        if (r.status) ok += group.ids.length;
        else this.$toast.error(r.msg || r.message, "Error");
      }
      this.loading_action = false;
      if (ok) this.$toast.success(ok + " movimiento(s) del banco registrados y conciliados.", "OK");
      this.load();
    },
    /**
     * Registra en Syscore líneas que solo están en el banco: abre el modal de tesorería con la cuenta
     * bancaria precargada (fecha = la más reciente). Con varias líneas pregunta si se cargan sumadas en una
     * línea o línea por línea. Al crearlo, se concilia contra esas líneas. Las plantillas se eligen en el modal.
     */
    openCreateMovement(lineas) {
      if (!lineas.length) return;
      if (!this.tesoreriaCreateAccess) {
        this.$toast.error("No tenés permiso para crear movimientos de tesorería.", "Error");
        return;
      }
      if (lineas.some((l) => l.amount > 0) && lineas.some((l) => l.amount < 0)) {
        this.$toast.error("No se pueden registrar créditos y débitos del banco en un mismo movimiento.", "Error");
        return;
      }
      if (lineas.length > 1) {
        this.modo_lineas = lineas;
        this.dialog_modo_movimiento = true;
        return;
      }
      this.buildMovement(lineas, "sum");
    },
    onModoMovimiento(mode) {
      this.dialog_modo_movimiento = false;
      this.buildMovement(this.modo_lineas, mode);
    },
    //Motivo de una línea del banco: "concepto · referencia" (o código si no hay referencia), sin repetir valores
    bankLineDescription(l) {
      const parts = [];
      [l.concept, l.reference || l.bank_code].forEach((v) => {
        const t = (v || "").trim();
        if (t && !parts.includes(t)) parts.push(t);
      });
      return parts.length ? parts.join(" · ").substring(0, 255) : null;
    },
    buildMovement(lineas, mode) {
      const account = this.c.money_account;
      const total = lineas.reduce((s, l) => s + l.amount, 0);
      const amount = formatUtils.formatNumber(Math.abs(total));
      const concept = lineas.length === 1 ? lineas[0].concept : lineas.length + " movimientos del banco";
      const lineByLine = mode === "line_by_line";
      const bank = {
        money_account: { id: account.id, name: account.name },
        amount_debe: total > 0 ? amount : null,
        amount_haber: total < 0 ? amount : null,
        description: concept,
      };
      this.movementLineas = lineas;
      this.movementPrefill = lineByLine ? { ...bank, line_by_line: true } : bank;
      const bankDetail = { ...bank, concepto: null, cost_center: null };
      let details;
      if (lineByLine) {
        //Una línea de banco por el total y una fila por movimiento (cuenta a elegir, importe del lado opuesto al banco)
        const rows = [...lineas]
          .sort((x, y) => (x.date < y.date ? -1 : x.date > y.date ? 1 : x.id - y.id))
          .map((l) => {
            const n = formatUtils.formatNumber(Math.abs(l.amount));
            return {
              money_account: null,
              concepto: null,
              cost_center: null,
              amount_debe: l.amount < 0 ? n : null,
              amount_haber: l.amount > 0 ? n : null,
              description: this.bankLineDescription(l),
              from_line: true,
              //Solo para aparear con la plantilla: createItem arma el payload campo por campo y no lo envía
              bank_line: { concept: l.concept, reference: l.reference, bank_code: l.bank_code },
            };
          });
        details = [{ ...bankDetail, from_bank: true }, ...rows];
      } else {
        details = [bankDetail, { money_account: null, concepto: null, cost_center: null, amount_debe: bank.amount_haber, amount_haber: bank.amount_debe, description: null }];
      }
      this.movementItem = {
        id: "",
        date: lineas.map((l) => l.date).sort().pop(),
        clase_comprobante: null,
        emitter: null,
        concept: concept,
        reference: "Conciliación " + this.c.label,
        details,
      };
      this.dialog_movement = true;
    },
    onMovementCreated(movement) {
      if (!movement || !this.movementLineas.length) return;
      const account_id = this.c.money_account.id;
      //Solo el lado del banco (crédito = Debe, débito = Haber): la contrapartida puede usar la misma cuenta
      const bank_type = this.movementLineas.reduce((s, l) => s + l.amount, 0) > 0 ? "D" : "H";
      const detail_ids = (movement.details || [])
        .filter((d) => d.money_account && d.money_account.id === account_id && d.movement_type === bank_type)
        .map((d) => d.id);
      const linea_ids = this.movementLineas.map((l) => l.id);
      this.movementLineas = [];
      this.run(conciliacionBancariaService.match(this.id, { linea_ids, detail_ids }));
    },
    createRule(linea) {
      this.regla_item = {
        id: 0,
        match_field: linea.bank_code ? "bank_code" : "description",
        pattern: linea.bank_code || linea.concept,
        money_account: { id: this.c.money_account.id, name: this.c.money_account.name },
        tesoreria_movement_template: null,
        enabled: true,
      };
      this.dialog_regla = true;
    },
    closeConciliacion() {
      this.dialog_close = false;
      this.loading_close = true;
      this.run(conciliacionBancariaService.close(this.id)).finally(() => { this.loading_close = false; });
    },
    reopen() {
      this.dialog_reopen = false;
      this.run(conciliacionBancariaService.reopen(this.id));
    },
    exportTab(tab) {
      this.exportExcel(this.section + "/export/" + this.id, { tab }, "Conciliacion-" + this.c.number + "-" + tab + ".xlsx");
    },
    print() {
      this.loading_print = true;
      conciliacionBancariaService
        .print(this.id)
        .then((response) => {
          if (!generalService.downloadPdf(response, "Conciliacion-" + this.c.number + ".pdf", true)) {
            this.$toast.error(response.msg || "No se pudo generar el PDF.", "Error");
          }
        })
        .finally(() => { this.loading_print = false; });
    },
  },
};
</script>

<style scoped>
/* v-window oculta el overflow y rompería la barra de selección sticky */
.tabs-visible {
  overflow: visible;
}
.head-icon {
  margin-top: 2px;
}
</style>
